import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { Plus, X, Sparkles, Pill } from 'lucide-react';
import { Drug } from '../../types/drug.types';
import { CreatePrescriptionItemDto } from '../../types/prescription.types';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { DrugForm } from '../drugs/DrugForm';
import { SearchableSelect } from '../common/SearchableSelect';
import { prescriptionsApi, DrugDoseSuggestion } from '../../api/prescriptions.api';
import { useQuery } from '@tanstack/react-query';
import { useCreateDrug } from '../../hooks/useDrugs';
import { CreateDrugDto } from '../../types/drug.types';
import toast from 'react-hot-toast';

interface DrugSelectorProps {
  drugs: Drug[];
  selectedDrugs: CreatePrescriptionItemDto[];
  onAddDrug: (drug: CreatePrescriptionItemDto) => void;
  onRemoveDrug: (index: number) => void;
  patientId?: number;
}

export const DrugSelector: React.FC<DrugSelectorProps> = ({
  drugs,
  selectedDrugs,
  onAddDrug,
  onRemoveDrug,
  patientId,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [selectedDrugId, setSelectedDrugId] = useState<number | ''>('');
  const [dose, setDose] = useState('');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isAddDrugModalOpen, setIsAddDrugModalOpen] = useState(false);

  const createDrugMutation = useCreateDrug();

  // All drugs are available for selection (filtering is handled by SearchableSelect)
  const filteredDrugs = drugs;

  // Check if form is valid
  const isFormValid = React.useMemo(() => {
    const hasDrug = !!selectedDrugId;
    const hasDose = !!dose?.trim();
    const durationValue = parseInt(duration, 10);
    const hasValidDuration = !!duration && !isNaN(durationValue) && durationValue > 0;
    return hasDrug && hasDose && hasValidDuration;
  }, [selectedDrugId, dose, duration]);

  // Get dose suggestions when drug is selected and patientId is available
  const { data: suggestions } = useQuery<DrugDoseSuggestion[]>({
    queryKey: ['drugDoseSuggestions', patientId, selectedDrugId],
    queryFn: () => prescriptionsApi.getDrugDoseSuggestions(patientId!, selectedDrugId as number),
    enabled: !!patientId && selectedDrugId != null && selectedDrugId !== '',
    staleTime: 5 * 60 * 1000, // 5 minutes - cache suggestions
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Auto-fill with most recent suggestion when drug is selected
  useEffect(() => {
    if (suggestions && suggestions.length > 0 && !dose && !duration) {
      const mostRecent = suggestions[0];
      setDose(mostRecent.dose);
      setDuration(mostRecent.duration.toString());
      if (mostRecent.notes) {
        setNotes(mostRecent.notes);
      }
    }
  }, [suggestions, dose, duration]);

  const handleAddDrug = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    setValidationError(null);
    
    if (!selectedDrugId) {
      setValidationError(t('prescriptions.selectDrugFirst', 'Please select a drug first'));
      return;
    }
    
    if (!dose || !dose.trim()) {
      setValidationError(t('prescriptions.doseRequired', 'Please enter the dose'));
      return;
    }
    
    const durationValue = parseInt(duration, 10);
    if (!duration || isNaN(durationValue) || durationValue <= 0) {
      setValidationError(t('prescriptions.durationRequired', 'Please enter a valid duration (greater than 0)'));
      return;
    }

    try {
      onAddDrug({
        drugId: selectedDrugId as number,
        dose: dose.trim(),
        duration: durationValue,
        notes: notes.trim() || undefined,
      });
      
      // Reset form
      setSelectedDrugId('');
      setDose('');
      setDuration('');
      setNotes('');
      setValidationError(null);
    } catch (error) {
      setValidationError(t('prescriptions.addDrugError', 'Failed to add drug. Please try again.'));
      console.error('Error adding drug:', error);
    }
  };

  const handleCreateDrug = async (data: CreateDrugDto) => {
    try {
      const newDrug = await createDrugMutation.mutateAsync(data);
      toast.success(t('drugs.drugCreated', 'Drug created successfully'));
      setIsAddDrugModalOpen(false);
      // Auto-select the newly created drug
      setSelectedDrugId(newDrug.id);
    } catch (error) {
      toast.error(t('drugs.drugCreatedError', 'Failed to create drug'));
    }
  };

  return (
    <div className="mb-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="d-flex align-items-center justify-content-between mb-4" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <h3 className="text-lg font-semibold mb-0" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          {t('prescriptions.medications')}
        </h3>
        {drugs.length > 0 && (
          <span className="badge bg-primary" style={{ fontSize: '0.75rem' }}>
            {drugs.length} {t('prescriptions.available', 'متاح')}
          </span>
        )}
      </div>

      <div className="mb-4 space-y-2">
        <div className="d-flex gap-2 mb-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="flex-grow-1">
            <SearchableSelect
              placeholder={
                drugs.length > 0 
                  ? t('prescriptions.selectDrug', 'اختر دواء...')
                  : t('prescriptions.noDrugsAvailable', 'لا توجد أدوية متاحة - أضف دواء جديد')
              }
              value={selectedDrugId}
              onChange={(value) => {
                const newDrugId = value != null && value !== '' ? (typeof value === 'string' ? parseInt(value, 10) : value) : '';
                setSelectedDrugId(newDrugId);
                if (newDrugId !== selectedDrugId) {
                  setDose('');
                  setDuration('');
                  setNotes('');
                }
              }}
              options={filteredDrugs.map((drug) => ({
                value: drug.id,
                label: drug.name,
              }))}
              searchable={true}
              disabled={drugs.length === 0}
              clearable={true}
            />
          </div>
          <Button
            variant="success"
            size="sm"
            onClick={() => setIsAddDrugModalOpen(true)}
            className="d-flex align-items-center gap-2 fw-semibold"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <Plus className="h-4 w-4" />
            {t('drugs.addNewDrug', 'Add New')}
          </Button>
        </div>

        {drugs.length === 0 ? (
          <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded">
            <p className="text-warning mb-2 fw-semibold">
              {t('drugs.noDrugsAvailable', 'لا توجد أدوية متاحة')}
            </p>
            <p className="text-warning small mb-2">
              {t('drugs.addDrugsFirst', 'يرجى إضافة أدوية جديدة أولاً')}
            </p>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={() => setIsAddDrugModalOpen(true)}
            >
              {t('drugs.addFirstDrug', 'إضافة أول دواء')}
            </button>
          </div>
        ) : filteredDrugs.length === 0 && (
          <div className="p-2 bg-info bg-opacity-10 border border-info rounded small text-info">
            {t('drugs.drugNotFound', 'لا يوجد دواء يطابق البحث')}
          </div>
        )}

        {/* Show suggestions if available */}
        {suggestions && suggestions.length > 0 && selectedDrugId && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <div className="d-flex align-items-center mb-2">
              <Sparkles className="h-4 w-4 text-blue-600 me-2" />
              <p className="text-sm font-medium text-blue-900 mb-0">
                {t('prescriptions.suggestions', 'Previous doses for this patient')}
              </p>
            </div>
            <div className="space-y-1">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setDose(suggestion.dose);
                    setDuration(suggestion.duration.toString());
                    setNotes(suggestion.notes || '');
                  }}
                  className="w-100 text-start btn btn-sm btn-outline-primary d-flex align-items-center justify-content-between mb-1"
                >
                  <span>
                    {suggestion.dose} - {suggestion.duration} {t('common.days')}
                    {suggestion.notes && (
                      <span className="text-muted ms-2">({suggestion.notes})</span>
                    )}
                  </span>
                  <span className="text-muted small">
                    {new Date(suggestion.lastUsedDate).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="row g-2">
          <div className="col-6">
            <Input
              placeholder={t('prescriptions.dose')}
              value={dose}
              onChange={(e) => {
                setDose(e.target.value);
                setValidationError(null);
              }}
              required
            />
          </div>
          <div className="col-6">
            <Input
              placeholder={t('prescriptions.duration')}
              type="number"
              min="1"
              value={duration}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow positive numbers
                if (value === '' || (!isNaN(parseInt(value, 10)) && parseInt(value, 10) > 0)) {
                  setDuration(value);
                  setValidationError(null);
                }
              }}
              required
            />
          </div>
        </div>

        <Input
          placeholder={t('prescriptions.drugNotes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {validationError && (
          <div className="alert alert-warning py-2 mb-2" role="alert">
            <small>{validationError}</small>
          </div>
        )}

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isFormValid) {
              handleAddDrug(e);
            }
          }}
          disabled={!isFormValid}
          type="button"
          className={`btn btn-secondary btn-sm w-100 d-flex align-items-center justify-content-center ${!isFormValid ? 'opacity-50' : ''}`}
          style={{ cursor: !isFormValid ? 'not-allowed' : 'pointer' }}
        >
          <Plus className="h-4 w-4 me-2" />
          {t('prescriptions.addDrug')}
        </button>
      </div>

      {selectedDrugs.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 mb-2">{t('prescriptions.selectedMedications')}</p>
          {selectedDrugs.map((item, index) => {
            const drug = drugs.find((d) => d.id === item.drugId);
            return (
              <div
                key={index}
                className="d-flex align-items-center justify-content-between bg-gray-50 p-3 rounded-lg"
              >
                <div>
                  <p className="fw-medium mb-1">{drug?.name}</p>
                  <p className="text-sm text-gray-600 mb-0">
                    {item.dose} - {item.duration} {t('common.days', 'days')}
                  </p>
                  {item.notes && (
                    <p className="text-sm text-gray-500 mb-0">{item.notes}</p>
                  )}
                </div>
                <button
                  onClick={() => onRemoveDrug(index)}
                  className="btn btn-link text-danger p-0"
                  type="button"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isAddDrugModalOpen}
        onClose={() => setIsAddDrugModalOpen(false)}
        title={
          <span className="d-flex align-items-center gap-2">
            <Pill className="h-5 w-5" />
            {t('drugs.addNewDrug', 'Add New Drug')}
          </span>
        }
        size="md"
      >
        <DrugForm
          onSubmit={handleCreateDrug}
          isLoading={createDrugMutation.isPending}
          onCancel={() => setIsAddDrugModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

